import React, {Fragment, useEffect, useState} from 'react';
import { useQuery } from 'react-query';
import {Helmet} from "react-helmet";
import Breadcrumb from "../../common/breadcrumb";
import DataTable from "react-data-table-component";
import dayjs from "dayjs";
import {Button, Input} from "reactstrap";
import {Calendar} from "react-feather";
import {Form} from "react-bootstrap";
import DatePicker, {registerLocale} from "react-datepicker";
import {debounce, throttle} from "lodash";

import "react-datepicker/dist/react-datepicker.css";
import { api } from "../../../utils/api";

import { ko } from 'date-fns/locale/ko';
registerLocale('ko', ko);

const OrderList = () => {
    const aWeekAgo = new Date()
    aWeekAgo.setDate(aWeekAgo.getDate() - 14)

    const [totalRows, setTotalRows] = useState(0);

    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(10);

    // 정렬 필드
    const [sortField, setSortField] = useState('created_at');
    // 정렬 방식 (asc, desc)
    const [sortDirection, setSortDirection] = useState('DESC');

    const [startDate, setStartDate] = useState(aWeekAgo);
    const [endDate, setEndDate] = useState(new Date());
    const [query, setQuery] = useState('');
    const [tmpQuery, setTmpQuery] = useState(query);
    const [amountOperator, setAmountOperator] = useState('>=');
    const [amount, setAmount] = useState('');
    const [plan, setPlan] = useState('');
    const [planGroup, setPlanGroup] = useState('');

    const [searchField, setSearchField] = useState('user_email');

    const handleChange = (e) => {
        switch (e.target.name) {
            case 'search_field':
                setSearchField(e.target.value);
                setQuery('');
                setTmpQuery('');
                break;
            case 'user_email':
            case 'remarks':
                setQuery(e.target.value);
                setTmpQuery(e.target.value);
                break;
            case 'plan':
                setPlan(e.target.value);
                break;
            case 'plan_group':
                setPlanGroup(e.target.value);
                break;
        }
    }

    const columns = [
        {
            name: '번호',
            cell: (row, index) => new Intl.NumberFormat('ko-KR').format(currentPageSize * (currentPage - 1) + index + 1),
        },
        {
            name: '개통 고객 이메일',
            selector: row => row?.user?.email,
            sortable: true,
            sortField: 'user.email',
        },
        {
            name: '요금제',
            selector: row => row?.plan?.name,
            sortable: true,
            sortField: 'plan.name',
        },
        {
            name: '개통 금액',
            selector: row => new Intl.NumberFormat('ko-KR', {
                style: 'decimal',
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
            }).format(row.total_amount / 2),
            sortable: true,
            sortField: 'total_amount',
        },
        {
            name: '개통 시간',
            selector: row => dayjs(new Date(row.created_at * 1000)).format('YYYY-MM-DD HH:mm:ss'),
            sortable: true,
            sortField: 'created_at',
        },
    ];

    const { data: planList, isFetching: planListLoading } = useQuery({
        queryKey: 'getPlanList',
        queryFn: async () => {
            return await api(localStorage.auth_data).get('/v1/rhksflwk/plan/fetch', {})
        },
        staleTime: 30000,
    })

    const { data: planGroupList } = useQuery({
        queryKey: 'getGroupList',
        queryFn: async () => {
            return await api(localStorage.auth_data).get('/v1/rhksflwk/server/group/fetch')
        },
        staleTime: 30000,
    })


    const { data: orderList, isFetching: isLoading, refetch } = useQuery(['getOrderList', localStorage.auth_data ], async () => {
        const filters = [
            {
                key: 'server_group_id',
                condition: '=',
                value: planGroup,
            },
            {
                key: 'plan_id',
                condition: '=',
                value: plan,
            },
            {
                key: 'created_at',
                condition: '>=',
                value: startDate,
            },
            {
                key: 'created_at',
                condition: '<=',
                value: endDate,
            },
            {
                key: 'total_amount',
                condition: amountOperator,
                value: amount,
            }
        ];

        switch (searchField) {
            case 'user_email':
            case 'remarks':
                filters.push({
                    key: searchField,
                    condition: 'like',
                    value: query,
                });
                break;
        }

        const response = await api(localStorage.auth_data).get(`/v2/rhksflwk/reseller/order/list?pageSize=${currentPageSize}&current=${currentPage}`, {
            params: {
                sort: sortField,
                sort_type: sortDirection,
                filter: filters,
            }
        }, {
            gcTime: 1,
        })

        setTotalRows(response?.data?.data?.total || 0);

        return response.data
    }, {
        gcTime: 1,
    })

    const handleSearch = async (e) => {
        e.preventDefault()

        await refetch()
    }

    const handleSort = async (column, sortDirection) => {
        setSortField(column.sortField);
        setSortDirection(sortDirection);

        await refetch();
    };

    useEffect(() => {
        debounce(async () => {
            await refetch();
        }, 300)();
        return setQuery(tmpQuery)
    }, [searchField, tmpQuery, plan, planGroup, startDate, endDate, amountOperator, amount, currentPage, currentPageSize]);

        useEffect(() => {
            setCurrentPage(1);
            throttle(async () => {
                await refetch();
            }, 100)();
        }, [currentPageSize]);

    return <Fragment>
        <Helmet>
            <title>사용자 개통 내역</title>
        </Helmet>
        <Breadcrumb title="사용자 개통 내역" parent="대시보드" />
        <div className="container-fluid">
            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-body">
                            <Form onSubmit={handleSearch}>
                                <Form.Group className="row my-1">
                                    <Form.Label className="col-lg-2 d-flex align-items-center">검색</Form.Label>
                                    <div className="col">
                                        <Form.Select name="search_field" defaultValue="user_email" value={searchField} onChange={handleChange}>
                                            <option value="user_email">개통 고객 이메일</option>
                                            <option value="remarks">비고</option>
                                        </Form.Select>
                                    </div>
                                    <div className="col">
                                        <Form.Control name={searchField} type={searchField === 'user_email' ? 'email': 'text'} value={tmpQuery} onChange={handleChange} />
                                    </div>
                                </Form.Group>
                                <Form.Group className="row my-1">
                                    <Form.Label className="col-lg-2 d-flex align-items-center">요금제</Form.Label>
                                    <div className="col">
                                        <Input type="select" name="plan_group" defaultValue={planGroup} onChange={handleChange}>
                                            <option value="">전체 요금제 그룹</option>
                                            {planGroupList?.data?.data.map((group) => (
                                                <option key={group.id} value={group.id}>{group.name}</option>
                                            ))}
                                        </Input>
                                    </div>
                                    <div className="col">
                                        <Input type="select" name="plan" defaultValue={plan} onChange={handleChange}>
                                            <option value="">전체 요금제</option>
                                            {!planListLoading && planList?.data?.data.filter((plan) => {
                                                return planGroup === '' || (planGroup !== '' && parseInt(plan.group_id, 10) === parseInt(planGroup, 10))
                                            }).map((plan) => (
                                                <option key={plan.id} value={plan.id}>{plan.name}</option>
                                            ))}
                                        </Input>
                                    </div>
                                </Form.Group>
                                <Form.Group className="row my-1">
                                    <div className="col-lg-2 d-flex align-items-center">
                                        <Form.Label>개통 시간</Form.Label>
                                    </div>
                                    <div className="col">
                                        <DatePicker
                                            showIcon
                                            icon={<Calendar/>}
                                            className="form-control digits"
                                            selected={startDate}
                                            onChange={date => setStartDate(date)}
                                            showTimeSelect
                                            placeholderText="시작일"
                                            locale="ko"
                                            dateFormat="P"
                                        />
                                    </div>
                                    <div className="col">
                                        <DatePicker
                                            showIcon
                                            icon={<Calendar />}
                                            className="form-control digits"
                                            selected={endDate}
                                            onChange={date => setEndDate(date)}
                                            showTimeSelect
                                            placeholderText="종료일"
                                            locale="ko"
                                            dateFormat="P"
                                        />
                                    </div>
                                </Form.Group>
                                <Form.Group className="row my-1">
                                    <div className="col-lg-2 d-flex align-items-center">
                                        <Form.Label>개통 금액</Form.Label>
                                    </div>
                                    <div className="col">
                                        <Form.Select defaultValue={amountOperator} onChange={(e) => setAmountOperator(e.target.value)}>
                                            <option value=">=">이상</option>
                                            <option value="<=">이하</option>
                                            <option value="<">미만</option>
                                            <option value=">">초과</option>
                                            <option value="=">일치</option>
                                        </Form.Select>
                                    </div>
                                    <div className="col">
                                        <Form.Control
                                            type="text"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                        />
                                    </div>
                                </Form.Group>
                                <div className="my-2 text-end">
                                    <Button color="dark" onClick={() =>  {
                                        setSearchField('user_email')
                                        setQuery('')
                                        setTmpQuery('')
                                        setPlan('')
                                        setPlanGroup('')
                                        setStartDate(new Date().setDate(aWeekAgo.getDate() - 14))
                                        setEndDate(new Date())
                                        setAmountOperator('>=')
                                        setAmount('')
                                    }}>초기화</Button>
                                </div>
                            </Form>
                            <DataTable
                                columns={columns}
                                data={orderList?.data?.data}
                                sortServer
                                pagination
                                paginationServer
                                paginationTotalRows={totalRows}
                                paginationComponentOptions={{ rowsPerPageText: '한 페이지에 보이는 개수', rangeSeparatorText: '중' }}
                                progressPending={isLoading}
                                fixedHeader
                                onChangePage={page => {
                                    setCurrentPage(page)}
                                }
                                onChangeRowsPerPage={size => setCurrentPageSize(size)}
                                onSort={handleSort}
                                noDataComponent={<div style={{ margin: 15 }}>개통 내역이 없습니다.</div>}
                            />
                        </div>

                    </div>
                </div>
            </div>
        </div>
    </Fragment>
}

export default OrderList;